import { AffineThemeViewExtension } from '@affine/core/blocksuite/view-extensions/theme';
import { addSiblingAttachmentBlocks } from '@blocksuite/affine/blocks/attachment';
import type { CodeBlockComponent } from '@blocksuite/affine/blocks/code';
import { ParagraphViewExtension } from '@blocksuite/affine/blocks/paragraph/view';
import { ViewExtensionManager } from '@blocksuite/affine/ext-loader';
import { getInternalViewExtensions } from '@blocksuite/affine/extensions/view';
import {
  focusTextModel,
  getInlineEditorByModel,
} from '@blocksuite/affine/rich-text';
import { ViewportElementExtension } from '@blocksuite/affine/shared/services';
import { openFilesWith } from '@blocksuite/affine/shared/utils';
import { type BlockStdScope, TextSelection } from '@blocksuite/affine/std';
import {
  type SlashMenuContext,
  SlashMenuExtension,
  type SlashMenuItem,
} from '@blocksuite/affine/widgets/slash-menu';
import type { FrameworkProvider } from '@toeverything/infra';

export function annotationSpecs(framework: FrameworkProvider) {
  const manager = new ViewExtensionManager([
    ...getInternalViewExtensions(),
    AffineThemeViewExtension,
  ]);
  manager.configure(AffineThemeViewExtension, { framework });
  manager.configure(ParagraphViewExtension, {
    getPlaceholder: model =>
      model.props.type === 'text'
        ? "Write notes, or type '/' for options"
        : model.props.type === 'quote'
          ? 'Quote'
          : 'Heading',
  });
  return manager
    .get('page')
    .concat([ViewportElementExtension('.mpx-annotation-notes')]);
}

/** Reuse native commands, with exactly the nine choices in Frank's Kaneo reference. */
export function configureAnnotationSlashMenu(std: BlockStdScope) {
  const slash = std.get(SlashMenuExtension);
  const original = slash.config;
  const choices: Record<
    string,
    { name: string; group: SlashMenuItem['group']; aliases?: string[] }
  > = {
    Text: { name: 'Text', group: '0_Text@0' },
    'Heading 2': {
      name: 'Heading',
      group: '0_Text@1',
      aliases: ['Überschrift', 'heading'],
    },
    'Bulleted List': {
      name: 'Bullet list',
      group: '1_Lists@0',
      aliases: ['Aufzählungsliste'],
    },
    'To-do List': {
      name: 'To-do list',
      group: '1_Lists@1',
      aliases: ['To-do-Liste'],
    },
    'Numbered List': {
      name: 'Numbered list',
      group: '1_Lists@2',
      aliases: ['Nummerierte Liste'],
    },
    Quote: { name: 'Quote', group: '2_Insert@0', aliases: ['Zitat'] },
    'Code Block': {
      name: 'Code block',
      group: '2_Insert@1',
      aliases: ['Codeblock'],
    },
    Table: { name: 'Table', group: '2_Insert@2', aliases: ['Tabelle'] },
    Attachment: {
      name: 'File',
      group: '2_Insert@3',
      aliases: ['Datei', 'attachment'],
    },
  };
  slash.config = {
    disableWhen: original.disableWhen,
    items: ctx => {
      const items =
        typeof original.items === 'function'
          ? original.items(ctx)
          : original.items;
      return items
        .filter(item => choices[item.name])
        .map(item => {
          const choice = choices[item.name];
          return {
            ...item,
            name: choice.name,
            group: choice.group,
            ...(item.name === 'Attachment'
              ? {
                  action: ({ std, model }: SlashMenuContext) => {
                    (async () => {
                      const files = await openFilesWith('Any', false, {
                        fileSystemAccess: false,
                      });
                      if (!files?.length || std.store.readonly) return;
                      await addSiblingAttachmentBlocks(std, files, model);
                      if (model.text?.length === 0)
                        std.store.deleteBlock(model);
                    })().catch(console.error);
                  },
                }
              : {}),
            searchAlias: [
              ...(item.searchAlias ?? []),
              ...(choice.aliases ?? []),
            ],
          };
        });
    },
  };
  // Scope these overrides to the annotation editor only. Native list keymaps
  // already split items, number them and exit on an empty item.
  const enter = (raw: KeyboardEvent, flavour: string) => {
    if (raw.isComposing || std.store.readonly) return false;
    const selection = std.selection.find(TextSelection);
    if (!selection || !selection.isCollapsed()) return false;
    const model = std.store.getBlock(selection.from.blockId)?.model;
    if (!model || !model.text || model.flavour !== flavour) return false;
    const type = model.props.type;
    if (flavour === 'affine:paragraph' && type === 'text') return false;
    const parent = std.store.getParent(model);
    const inlineEditor =
      flavour === 'affine:code'
        ? (std.view.getBlock(model.id) as CodeBlockComponent | undefined)
            ?.inlineEditor
        : getInlineEditorByModel(std, model);
    const range = inlineEditor?.getInlineRange();
    if (!parent || !range) return false;
    const index = parent.children.indexOf(model);
    if (index < 0) return false;
    raw.preventDefault();
    std.store.captureSync();
    let id: string;
    if (!model.text.length) {
      if (flavour === 'affine:paragraph') {
        std.store.updateBlock(model, { type: 'text', collapsed: false });
        id = model.id;
      } else {
        id = std.store.addBlock('affine:paragraph', {}, parent, index);
        std.store.deleteBlock(model);
      }
    } else {
      const text = model.text.split(range.index, range.length);
      id = std.store.addBlock(
        flavour,
        {
          text,
          ...(flavour === 'affine:paragraph'
            ? { type, collapsed: false }
            : { language: model.props.language }),
        },
        parent,
        index + 1
      );
    }
    std.host.updateComplete
      .then(() => focusTextModel(std, id))
      .catch(console.error);
    return true;
  };
  const disposers = ['affine:paragraph'].map(flavour =>
    std.event.bindHotkey(
      {
        Enter: ctx => enter(ctx.get('keyboardState').raw, flavour),
      },
      { flavour }
    )
  );
  // Code's own block-level Enter binding takes precedence over flavour keymaps.
  // Capture only code Enter in this notes host before that native binding.
  const codeEnter = (event: KeyboardEvent) => {
    if (
      event.key !== 'Enter' ||
      event.shiftKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      event.defaultPrevented
    )
      return;
    // Shadow DOM retargets code keys to the page root. Resolve the selected
    // block through this editor's selection instead of event.target.
    if (enter(event, 'affine:code')) event.stopPropagation();
  };
  std.host.addEventListener('keydown', codeEnter, { capture: true });
  return () => {
    disposers.forEach(dispose => dispose());
    std.host.removeEventListener('keydown', codeEnter, { capture: true });
  };
}
